import api from './api';

export type AlertLevel = 'OK' | 'WARN' | 'CRITICAL';

/**
 * 서버 `apps/server/src/admin/operations/operations-health.service.ts`의
 * 인터페이스를 수동 동기화한 것. 서버 쪽 인터페이스 변경 시 함께 업데이트.
 */

export interface SystemHealth {
  cpuUsage: number;
  loadAvg1m: number;
  loadAvg5m: number;
  memTotalMb: number;
  memUsedMb: number;
  memUsedPct: number;
  swapTotalMb: number;
  swapUsedMb: number;
  swapUsedPct: number;
  diskUsedGb: number;
  diskTotalGb: number;
  diskUsedPct: number;
  uptimeDays: number;
  alertLevel: AlertLevel;
  errorMessage?: string;
}

export interface DatabaseHealth {
  pingMs: number | null;
  activeConnections: number | null;
  slowQueries: number | null;
  alertLevel: AlertLevel;
  errorMessage?: string;
}

export interface RedisHealth {
  pingMs: number | null;
  memoryMb: number | null;
  memoryHuman: string | null;
  keys: number | null;
  alertLevel: AlertLevel;
  errorMessage?: string;
}

export interface PdfLastCleanup {
  at: string | null;
  killedCount: number;
  hasError: boolean;
}

export interface PdfHealth {
  chromeProcessCount: number | null;
  activeBrowserRunning: boolean;
  totalProcessed: number;
  totalFailed: number;
  lastFailureAt: string | null;
  lastCleanup: PdfLastCleanup;
  alertLevel: AlertLevel;
  errorMessage?: string;
}

export interface ExternalApiCall {
  lastCallAt: string | null;
  success: boolean | null;
  latencyMs: number | null;
  /** NTS validate 응답의 valid_msg (MISMATCH 원인 확인용) */
  lastValidMsg?: string;
  errorMessage?: string;
}

export interface ExternalApisHealth {
  nts: ExternalApiCall;
  publicData: ExternalApiCall;
  sms: ExternalApiCall;
  aiProviders: {
    gemini: ExternalApiCall;
    claude: ExternalApiCall;
    openai: ExternalApiCall;
  };
  alertLevel: AlertLevel;
  errorMessage?: string;
}

export interface CronTask {
  name: string;
  lastRunAt: string | null;
  success: boolean | null;
  durationMs: number | null;
  lastError: string | null;
}

export interface QueuesHealth {
  photoAnalysis: { active: number; waiting: number; failed: number } | null;
  import: { active: number } | null;
  match: { active: number } | null;
  alertLevel: AlertLevel;
  errorMessage?: string;
}

export interface VerificationHealth {
  pending: number;
  escalated: number;
  businessClosed: number;
  alertLevel: AlertLevel;
  errorMessage?: string;
}

export interface AiUsageHealth {
  callsToday: number;
  costToday: number;
  callsThisMonth: number;
  costThisMonth: number;
  errorMessage?: string;
}

export interface OperationsHealthData {
  system: SystemHealth;
  database: DatabaseHealth;
  redis: RedisHealth;
  pdf: PdfHealth;
  externalApis: ExternalApisHealth;
  cronTasks: CronTask[];
  queues: QueuesHealth;
  verification: VerificationHealth;
  aiUsage: AiUsageHealth;
  collectedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: null | { code: string; message: string };
}

export async function getOperationsHealth(): Promise<OperationsHealthData> {
  const res = await api.get<ApiResponse<OperationsHealthData>>('/admin/operations/health');
  return res.data.data;
}
